import { useState } from 'react';
import { useCustomQuestion } from '~/lib/CustomQuestions/CustomQuestionRegistry';
import { CustomQuestionMode } from '~/lib/CustomQuestions/CustomQuestionApi';
import useCustomQuestionFrame from '~/lib/CustomQuestions/useCustomQuestionFrame';
import type { FormQuestion } from '~/lib/Api.types';

type CustomQuestionAnswerProps = {
    answer: any;
    question: FormQuestion;
    /** Worked out server side, and what every other place shows */
    summary?: string;
    answeredAt?: string | null;
};

/**
 * A custom question's answer, as an admin reads it.
 *
 * The summary is the answer as far as the rest of the product is concerned -
 *  it is what emails, the submission PDF, exports and the submissions list
 *  show, because none of those can run the question's code. A question that
 *  sets the rendersAnswer flag additionally gets to draw it here, in its own
 *  frame with breely.mode === 'answer', and the summary stays one click away
 *  so a broken render never hides the data.
 */
const CustomQuestionAnswer = ({
    answer,
    question,
    summary,
    answeredAt,
}: CustomQuestionAnswerProps) => {
    const definition = useCustomQuestion(question?.settings?.customQuestionId);
    const [showsRaw, setShowsRaw] = useState(false);

    const rendersAnswer = !!definition?.flags?.rendersAnswer;
    const text = (summary ?? '').trim();

    if (!definition || !rendersAnswer) {
        return <AnswerText text={text} answer={answer} />;
    }

    return (
        <div>
            {showsRaw ? (
                <AnswerText text={text} answer={answer} />
            ) : (
                <AnswerFrame
                    definition={definition}
                    question={question}
                    answer={answer}
                    answeredAt={answeredAt}
                    summary={text}
                />
            )}

            <button
                type="button"
                className="text-gray-500 text-sm mt-1"
                onClick={() => setShowsRaw(!showsRaw)}
            >
                {showsRaw ? 'Show as it was answered' : 'Show the raw answer'}
            </button>
        </div>
    );
};

const AnswerFrame = ({
    definition,
    question,
    answer,
    answeredAt,
    summary,
}: {
    definition: any;
    question: FormQuestion;
    answer: any;
    answeredAt?: string | null;
    summary: string;
}) => {
    const { frameRef, srcDoc, height, isReady, hasTimedOut } =
        useCustomQuestionFrame({
            definition,
            mode: CustomQuestionMode.ANSWER,
            settings: question?.settings,
            value: answer,
            answeredAt,
            questionId: question?.id,
        });

    // Whatever the question's code does with it, the answer it is handed came
    //  from a client. The frame is the containment for that
    if (hasTimedOut) {
        return <AnswerText text={summary} answer={answer} />;
    }

    return (
        <iframe
            ref={frameRef}
            title={definition.name || 'Answer'}
            srcDoc={srcDoc}
            sandbox="allow-scripts"
            referrerPolicy="no-referrer"
            scrolling="no"
            className={`w-full block border-0 ${isReady ? 'opacity-100' : 'opacity-0'}`}
            style={{ height: `${height}px` }}
        />
    );
};

const AnswerText = ({ text, answer }: { text: string; answer: any }) => {
    if (text) {
        return (
            <span className="leading-tight whitespace-pre-wrap">{text}</span>
        );
    }

    const raw =
        typeof answer === 'string' ? answer : JSON.stringify(answer ?? '');

    if (!raw || raw === '""' || raw === 'null') {
        return <span className="text-gray-500 italic">No answer</span>;
    }

    return (
        <span className="leading-tight whitespace-pre-wrap break-all">
            {raw}
        </span>
    );
};

export default CustomQuestionAnswer;
